import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const DocumentsStep = ({ formData, updateFormData, errors }) => {
  const [dragActive, setDragActive] = useState({});

  const documentTypes = [
    {
      id: 'kvk',
      name: 'KvK Uittreksel',
      description: 'Recent uittreksel Kamer van Koophandel (max. 3 maanden oud)',
      required: true,
      acceptedFormats: '.pdf,.jpg,.jpeg,.png'
    },
    {
      id: 'insurance',
      name: 'Aansprakelijkheidsverzekering',
      description: 'Bewijs van geldige bedrijfsaansprakelijkheidsverzekering',
      required: true,
      acceptedFormats: '.pdf,.jpg,.jpeg,.png'
    },
    {
      id: 'certifications',
      name: 'Certificaten & Keurmerken',
      description: 'Relevante certificaten (ISSO, RVO, etc.)',
      required: false,
      acceptedFormats: '.pdf,.jpg,.jpeg,.png'
    },
    {
      id: 'permits',
      name: 'Vergunningen',
      description: 'Installatievergunningen en andere relevante documenten',
      required: false,
      acceptedFormats: '.pdf,.jpg,.jpeg,.png'
    }
  ];

  const handleDrag = (e, docType) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(prev => ({ ...prev, [docType]: true }));
    } else if (e?.type === 'dragleave') {
      setDragActive(prev => ({ ...prev, [docType]: false }));
    }
  };

  const handleDrop = (e, docType) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(prev => ({ ...prev, [docType]: false }));
    
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFileUpload(e?.dataTransfer?.files?.[0], docType);
    }
  };

  const handleFileUpload = (file, docType) => {
    // Mock file upload - in real implementation, this would upload to server
    const mockUploadedFile = {
      name: file?.name,
      size: file?.size,
      type: file?.type,
      uploadDate: new Date()?.toISOString(),
      url: URL.createObjectURL(file) // Mock URL
    };

    const currentDocuments = formData?.documents || {};
    updateFormData({
      documents: {
        ...currentDocuments,
        [docType]: mockUploadedFile
      }
    });
  };

  const handleFileInputChange = (e, docType) => {
    if (e?.target?.files && e?.target?.files?.[0]) {
      handleFileUpload(e?.target?.files?.[0], docType);
    }
  };

  const handleRemoveDocument = (docType) => {
    const currentDocuments = formData?.documents || {};
    const updatedDocuments = { ...currentDocuments };
    delete updatedDocuments?.[docType];
    updateFormData({ documents: updatedDocuments });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">
          Documenten & Certificaten
        </h2>
        <p className="text-muted-foreground">
          Upload de vereiste documenten voor verificatie van uw bedrijf. Dit helpt bij het opbouwen van vertrouwen met klanten.
        </p>
      </div>
      <div className="space-y-6">
        {documentTypes?.map((docType) => {
          const uploadedDoc = (formData?.documents || {})?.[docType?.id];
          const isActive = dragActive?.[docType?.id];

          return (
            <div key={docType?.id} className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-heading font-medium text-foreground flex items-center space-x-2">
                    <Icon name="FileText" size={20} />
                    <span>{docType?.name}</span>
                    {docType?.required && (
                      <span className="text-error text-sm">*</span>
                    )}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {docType?.description}
                  </p>
                </div>
                {docType?.required && (
                  <div className="bg-error/10 text-error px-2 py-1 rounded text-xs font-medium">
                    Verplicht
                  </div>
                )}
              </div>
              {!uploadedDoc ? (
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-micro ${
                    isActive
                      ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                  }`}
                  onDragEnter={(e) => handleDrag(e, docType?.id)}
                  onDragLeave={(e) => handleDrag(e, docType?.id)}
                  onDragOver={(e) => handleDrag(e, docType?.id)}
                  onDrop={(e) => handleDrop(e, docType?.id)}
                >
                  <Icon name="Upload" size={32} className="text-muted-foreground mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-foreground mb-2">
                    Sleep bestanden hierheen
                  </h4>
                  <p className="text-muted-foreground mb-4">
                    of klik om bestanden te selecteren
                  </p>
                  <input
                    type="file"
                    accept={docType?.acceptedFormats}
                    onChange={(e) => handleFileInputChange(e, docType?.id)}
                    className="hidden"
                    id={`file-${docType?.id}`}
                  />
                  <label htmlFor={`file-${docType?.id}`}>
                    <Button variant="outline" asChild>
                      <span className="cursor-pointer">
                        Bestand Selecteren
                      </span>
                    </Button>
                  </label>
                  <p className="text-xs text-muted-foreground mt-2">
                    Ondersteunde formaten: PDF, JPG, PNG (max. 10MB)
                  </p>
                </div>
              ) : (
                <div className="bg-success/5 border border-success/20 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                        <Icon name="FileCheck" size={20} color="var(--color-success)" />
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground">
                          {uploadedDoc?.name}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {formatFileSize(uploadedDoc?.size)} • Geüpload op {new Date(uploadedDoc.uploadDate)?.toLocaleDateString('nl-NL')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Eye"
                        onClick={() => window.open(uploadedDoc?.url, '_blank')}
                      >
                        Bekijken
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Trash2"
                        onClick={() => handleRemoveDocument(docType?.id)}
                      >
                        Verwijderen
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              {errors?.[docType?.id] && (
                <div className="text-error text-sm mt-2">
                  {errors?.[docType?.id]}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Additional Information */}
      <div className="bg-muted/30 rounded-lg p-6">
        <h3 className="text-lg font-heading font-medium text-foreground mb-4 flex items-center space-x-2">
          <Icon name="Info" size={20} />
          <span>Belangrijke Informatie</span>
        </h3>
        
        <div className="space-y-3 text-sm text-muted-foreground">
          <div className="flex items-start space-x-2">
            <Icon name="Check" size={16} className="text-success mt-0.5" />
            <span>Alle documenten worden veilig opgeslagen en alleen gebruikt voor verificatie</span>
          </div>
          <div className="flex items-start space-x-2">
            <Icon name="Check" size={16} className="text-success mt-0.5" />
            <span>Verificatie duurt meestal 1-2 werkdagen</span>
          </div>
          <div className="flex items-start space-x-2">
            <Icon name="Check" size={16} className="text-success mt-0.5" />
            <span>Geverifieerde providers krijgen een vertrouwensbadge</span>
          </div>
          <div className="flex items-start space-x-2">
            <Icon name="AlertCircle" size={16} className="text-warning mt-0.5" />
            <span>Zorg ervoor dat alle documenten recent en geldig zijn</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentsStep;